import React, { useState } from "react";
import { useAgingReport, useCashFlowReport, useDreReport, useRevenueByApplicationReport, exportReportCsv } from "../../api/reports";
import { formatBRL, formatDate } from "../../utils/money";

type TabKey = "dre" | "cash-flow" | "revenue" | "aging";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "dre", label: "DRE" },
  { key: "cash-flow", label: "Fluxo de caixa" },
  { key: "revenue", label: "Receita por aplicação" },
  { key: "aging", label: "Aging (30/60/90)" },
];

function ExportButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn btn-secondary btn-sm ms-auto" onClick={onClick}>
      Exportar CSV
    </button>
  );
}

function DreTab() {
  const { data, isLoading } = useDreReport(6);
  if (isLoading) return <div className="text-secondary">Carregando…</div>;
  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex">
        <ExportButton onClick={() => exportReportCsv("/reports/dre", "dre.csv", { months: 6 })} />
      </div>
      <div className="nt-table-wrap">
        <table className="table nt-table-fade table-striped align-middle">
          <thead>
            <tr>
              <th>Mês</th>
              <th className="text-end">Receita</th>
              <th className="text-end">Despesas</th>
              <th className="text-end">Resultado</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((r) => (
              <tr key={r.mes}>
                <td>{r.mes}</td>
                <td className="text-end nt-tabular">{formatBRL(r.receita)}</td>
                <td className="text-end nt-tabular">{formatBRL(r.despesas)}</td>
                <td className="text-end nt-tabular" style={{ color: r.resultado >= 0 ? "var(--sp-success)" : "var(--sp-danger)", fontWeight: 700 }}>
                  {formatBRL(r.resultado)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CashFlowTab() {
  const { data, isLoading } = useCashFlowReport(12);
  if (isLoading) return <div className="text-secondary">Carregando…</div>;
  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex">
        <ExportButton onClick={() => exportReportCsv("/reports/cash-flow", "fluxo-de-caixa.csv", { months: 12 })} />
      </div>
      <div className="nt-table-wrap">
        <table className="table nt-table-fade table-striped align-middle">
          <thead>
            <tr>
              <th>Mês</th>
              <th className="text-end">Recebido</th>
              <th className="text-end">Pago</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((r) => (
              <tr key={r.mes}>
                <td>{r.mes}</td>
                <td className="text-end nt-tabular">{formatBRL(r.recebido)}</td>
                <td className="text-end nt-tabular">{formatBRL(r.pago)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RevenueTab() {
  const { data, isLoading } = useRevenueByApplicationReport();
  if (isLoading) return <div className="text-secondary">Carregando…</div>;
  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex">
        <ExportButton onClick={() => exportReportCsv("/reports/revenue-by-application", "receita-por-aplicacao.csv")} />
      </div>
      <div className="nt-table-wrap">
        <table className="table nt-table-fade table-striped align-middle">
          <thead>
            <tr>
              <th>Aplicação</th>
              <th className="text-end">MRR</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((r) => (
              <tr key={r.nome}>
                <td>{r.nome}</td>
                <td className="text-end nt-tabular">{formatBRL(r.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgingTab() {
  const { data, isLoading } = useAgingReport();
  if (isLoading) return <div className="text-secondary">Carregando…</div>;
  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex">
        <ExportButton onClick={() => exportReportCsv("/reports/aging", "aging.csv")} />
      </div>
      <div className="row g-3">
        {data?.summary.map((s) => (
          <div className="col-6 col-md-3" key={s.faixa}>
            <div className="nt-card p-3">
              <div className="text-secondary" style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {s.faixa} dias
              </div>
              <div className="nt-tabular" style={{ fontFamily: "var(--font-heading)", fontSize: 18 }}>
                {formatBRL(s.total)}
              </div>
              <div className="text-secondary" style={{ fontSize: 11.5 }}>
                {s.quantidade} título(s)
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="nt-table-wrap">
        <table className="table nt-table-fade table-striped align-middle">
          <thead>
            <tr>
              <th>Título</th>
              <th>Cliente</th>
              <th>Vencimento</th>
              <th className="text-end">Dias em atraso</th>
              <th>Faixa</th>
              <th className="text-end">Valor</th>
            </tr>
          </thead>
          <tbody>
            {data?.detail.map((d) => (
              <tr key={d.codigo}>
                <td className="text-secondary">{d.codigo}</td>
                <td>{d.cliente}</td>
                <td className="nt-tabular">{formatDate(d.vencimento)}</td>
                <td className="text-end nt-tabular">{d.diasEmAtraso}</td>
                <td>{d.faixa}</td>
                <td className="text-end nt-tabular">{formatBRL(d.valor)}</td>
              </tr>
            ))}
            {data?.detail.length === 0 && (
              <tr>
                <td colSpan={6} className="text-secondary text-center py-4">
                  Nenhum título vencido.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const [tab, setTab] = useState<TabKey>("dre");

  return (
    <div className="d-flex flex-column gap-4">
      <div className="nt-seg align-self-start" role="tablist" aria-label="Relatório">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? "on" : undefined}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dre" && <DreTab />}
      {tab === "cash-flow" && <CashFlowTab />}
      {tab === "revenue" && <RevenueTab />}
      {tab === "aging" && <AgingTab />}
    </div>
  );
}
